// /src/components/core/NotificationsDropdown.jsx
// In-app notification inbox dropdown (Notifications Commit 1).
// Loads the recipient's latest notifications via the get_notifications RPC
// (SECURITY DEFINER — RLS-safe joins for actor / group / article names), renders
// one translated line per type, marks read on click, and navigates to the target.
// Default view is unread-only; a footer toggle reveals read rows on demand (design note
// docs/briefs/2026-09-22-notification-lifecycle-and-moderation-transparency-design-note.md §2).
// Mirrors IssuesDropdown's backdrop + fixed-panel shape. All strings via t().

import { useState, useEffect } from 'react';
import { X, Bell, Check, ChevronRight } from 'lucide-react';
import { supabaseFetch } from '../../lib/supabase';
import { tokens } from '../../styles/designTokens';
import StandardButton from './StandardButton';
import { useTranslation } from '../../i18n/useTranslation';
import { formatTimeAgo } from '../../utils/formatText';

function NotificationsDropdown({ isOpen, onClose, currentUser }) {
  const { t, formattingLocale } = useTranslation('notifications');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Read rows are hidden unless asked for. The component stays mounted while closed
  // (it returns null), so this is reset on every open rather than left from last time.
  const [showRead, setShowRead] = useState(false);

  useEffect(() => {
    if (isOpen && currentUser?.id) {
      setShowRead(false);
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      // 100, not 30: the list is filtered client-side to unread, and the header badge counts
      // up to 100 unread (HeaderButtonsFeed loadUnread). A smaller window here could show
      // "Nothing new" under a non-zero badge for anyone with more than 30 rows. The two can
      // still disagree past 100 rows in total — a server-side unread filter would close that.
      const { data, error } = await supabaseFetch.rpc('get_notifications', { p_limit: 100 });
      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }
      setItems(data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Translated message line per type. Actor falls back to the shared
  // removed-author label when the actor has since deleted their account.
  const lineFor = (n) => {
    const actor = n.actor_name || t('common:removed_author');
    switch (n.type) {
      case 'comment':            return t('type_comment',            { actor, title: n.article_title || '' });
      case 'group_chat':         return t('type_group_chat',         { actor, group: n.group_name || '' });
      case 'group_article':      return t('type_group_article',      { actor, group: n.group_name || '' });
      case 'group_event':        return t('type_group_event',        { actor, group: n.group_name || '' });
      case 'join_request':       return t('type_join_request',       { actor, group: n.group_name || '' });
      // Names the applicant group, not the person who filed it (4C §4) — get_notifications
      // resolves applicant_group_name via notifications.applicant_group_id, independent of
      // group_name (which here is the FEDERATION, the recipient's own group).
      case 'federation_request': return t('type_federation_request', { group: n.applicant_group_name || '' });
      // One-time prompt to owners of groups that were private before the three types (addendum 5
      // R4): names the type the group was given, links to its settings.
      case 'group_type_check_findable':   return t('type_group_type_check_findable',   { group: n.group_name || '' });
      case 'group_type_check_invitation': return t('type_group_type_check_invitation', { group: n.group_name || '' });
      // A Public group that still approves new members must choose (brief 2026-10-04 R2).
      case 'group_type_check_public_approval': return t('type_group_type_check_public_approval', { group: n.group_name || '' });
      default:                   return '';
    }
  };

  // Canonical "open item by id" path is the URL hash (parseHash → handleHashNavigation
  // in App.js), the same deep-link route share links and the chat permalink use — NOT
  // ournet:navigate (which has no open-item-by-id branch). Events are articles → #/event.
  // Comments open the article (at top); routing an article-comment through #/chat misroutes it.
  const hashFor = (n) => {
    switch (n.type) {
      case 'comment':
      case 'group_article':      return n.article_id ? `#/article/${n.article_id}` : null;
      case 'group_event':        return n.article_id ? `#/event/${n.article_id}`   : null;
      case 'group_chat':         return n.chat_id    ? `#/chat/${n.chat_id}`       : null;
      case 'join_request':
      case 'federation_request': return n.community_id ? `#/group_requests/${n.community_id}` : null;
      // Must stay identical to get_push_payload's hash for the same types (Architecture § Notifications).
      case 'group_type_check_findable':
      case 'group_type_check_invitation':
      case 'group_type_check_public_approval': return n.community_id ? `#/group_settings/${n.community_id}` : null;
      default:                   return null;
    }
  };

  const markRead = async (id) => {
    await supabaseFetch.update('notifications', { is_read: true }, { eq: { id } });
    window.dispatchEvent(new CustomEvent('ournet:notifications-updated'));
  };

  const handleRowClick = async (n) => {
    if (!n.is_read) {
      await markRead(n.id);
      setItems(prev => prev.map(i => (i.id === n.id ? { ...i, is_read: true } : i)));
    }
    const hash = hashFor(n);
    onClose();
    if (hash) {
      // Setting the hash to its current value won't fire 'hashchange' (e.g. tapping a
      // notification for the item already open) — clear first so the route re-fires.
      if (window.location.hash === hash) window.location.hash = '';
      window.location.hash = hash;
    }
  };

  const handleMarkAllRead = async () => {
    if (!currentUser?.id) return;
    await supabaseFetch.update('notifications', { is_read: true }, {
      eq: { recipient_id: currentUser.id, is_read: false }
    });
    setItems(prev => prev.map(i => ({ ...i, is_read: true })));
    window.dispatchEvent(new CustomEvent('ournet:notifications-updated'));
  };

  if (!isOpen) return null;

  const hasUnread = items.some(i => !i.is_read);
  const hasRead = items.some(i => i.is_read);
  const visibleItems = showRead ? items : items.filter(i => !i.is_read);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)', zIndex: 9998, pointerEvents: 'auto'
        }}
      />

      {/* Dropdown Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: '60px', right: '20px', width: '380px',
          maxHeight: 'calc(100vh - 200px)', background: 'white', borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', pointerEvents: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F9FAFB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Bell size={20} color="#024B99" />
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937' }}>
              {t('inbox_title')}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {hasUnread && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: '600', color: '#024B99', padding: '4px 8px'
                }}
              >
                {t('mark_all_read')}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <X size={20} color="#6B7280" />
            </button>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px' }}>
          {loading ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6B7280' }}>
              {t('loading')}
            </div>
          ) : visibleItems.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6B7280' }}>
              <Check size={32} color="#10B981" style={{ marginBottom: '8px' }} />
              <div>{t('empty')}</div>
            </div>
          ) : (
            visibleItems.map(n => (
              <div
                key={n.id}
                onClick={() => handleRowClick(n)}
                style={{
                  padding: '12px', marginBottom: '8px', cursor: 'pointer',
                  background: n.is_read ? '#FFFFFF' : '#EFF6FF',
                  borderRadius: '8px',
                  border: n.is_read ? '1px solid #E5E7EB' : '1px solid #BFDBFE',
                  transition: 'background 0.2s ease', position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  {/* Unread dot */}
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginTop: '5px',
                    background: n.is_read ? 'transparent' : '#024B99'
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', color: '#1F2937', lineHeight: '1.4', wordBreak: 'break-word' }}>
                      {lineFor(n)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                      {formatTimeAgo(n.created_at, t, formattingLocale)}
                    </div>
                  </div>
                  {/* Right chevron — affordance that the row opens its target */}
                  {hashFor(n) && (
                    <ChevronRight
                      size={18}
                      color={tokens.colors.textSecondary}
                      style={{ flexShrink: 0, alignSelf: 'center' }}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer — show/hide read rows. Outside the scrolling list so it stays reachable
            on short screens. */}
        {!loading && hasRead && (
          <div style={{
            flexShrink: 0, padding: '12px 16px', borderTop: `1px solid ${tokens.colors.borderSecondary}`,
            display: 'flex', justifyContent: 'center'
          }}>
            <StandardButton
              variant="inline"
              label={showRead ? t('hide_read') : t('show_read')}
              onClick={() => setShowRead(v => !v)}
              inlineTextColor={tokens.colors.textSecondary}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default NotificationsDropdown;
